import { StatusColors } from '@euler/components/Colors';
import Dialog from '@euler/components/dialog';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { delayed } from '@euler/utils';
import { useCreation, usePersistFn } from '@euler/utils/hooks';
import { useNavigation } from '@react-navigation/native';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Keyboard, StyleProp, TextInputProps, TextStyle } from 'react-native';
import { Subject } from 'rxjs';

type Event =
  | {
      type: 'loading';
      loading?: boolean;
    }
  | {
      type: 'error';
      error?: unknown;
    }
  | {
      type: 'button';
      button: 'cancel' | 'confirm';
      disabled?: boolean;
      text?: string;
    };

export type PromptScreenProps = {
  title: string;
  placeholder?: string;
  defaultValue?: string;
  message?: string;
  required?: boolean;
  cancelButtonText?: string;
  cancelButtonDisabled?: boolean;
  confirmButtonText?: string;
  confirmButtonDisabled?: boolean;
  destructive?: boolean;
  keyboardType?: TextInputProps['keyboardType'];
  inputStyle?: StyleProp<TextStyle>;
  errorPlacement?: 'above-input' | 'below-input';
  confirmOnClose?: boolean;
  onChange?: (value: string, notify$: Subject<Event>) => void;
  onConfirm?: (value: string, notify$: Subject<Event>) => Promise<void>;
  onCancel?: () => void;
  onClosed?: () => void;
};

export const PromptScreen = wrapNavigatorScreen(
  ({
    title,
    placeholder,
    message,
    defaultValue,
    keyboardType,
    destructive,
    inputStyle,
    confirmOnClose,
    required,
    onConfirm,
    onCancel,
    onChange,
    onClosed,
    ...props
  }: PromptScreenProps) => {
    const navigation = useNavigation();
    const { rpx } = useResponsiveContext();
    const event$ = useCreation(() => new Subject<Event>(), []);
    const [error, setError] = useState<unknown>();
    const [loading, setLoading] = useState(false);
    const [visible, setVisible] = useState(true);
    const [value, setValue] = useState(defaultValue ?? '');
    const [cancelButtonText, setCancelButtonText] = useState(
      props.cancelButtonText ?? 'Cancel',
    );
    const [confirmButtonText, setConfirmButtonText] = useState(
      props.confirmButtonText ?? 'OK',
    );
    const [cancelButtonDisabled, setCancelButtonDisabled] = useState(
      props.cancelButtonDisabled,
    );
    const [confirmButtonDisabled, setConfirmButtonDisabled] = useState(
      props.confirmButtonDisabled,
    );

    const nextAction = useRef<() => void>();

    const close = usePersistFn((callback?: () => void) => {
      setVisible(false);
      nextAction.current = callback;
    });

    const onClose = usePersistFn(() => {
      close();
    });

    const handleCancel = usePersistFn(() => {
      close(onCancel);
    });

    const handleConfirm = usePersistFn(async () => {
      try {
        Keyboard.dismiss();
        setError(undefined);
        if (confirmOnClose) {
          close(() => onConfirm?.(value, event$));
        } else {
          await onConfirm?.(value, event$);
          close();
        }
      } catch (e) {
        console.error(e);
        setError(e as any);
      } finally {
        setLoading(false);
      }
    });

    const onChangeText = useCallback(
      (s: string) => {
        onChange?.(s, event$);
        setValue(s);
      },
      [event$, onChange],
    );

    useEffect(() => {
      const sub = event$.subscribe(e => {
        if (e.type === 'loading') {
          setLoading(e.loading ?? true);
        } else if (e.type === 'error') {
          setError(e.error);
        } else if (e.type === 'button') {
          if (e.button === 'confirm') {
            if (e.disabled != null) {
              setConfirmButtonDisabled(e.disabled);
            }
            if (e.text != null) {
              setConfirmButtonText(e.text);
            }
          } else if (e.button === 'cancel') {
            if (e.disabled != null) {
              setCancelButtonDisabled(e.disabled);
            }
            if (e.text != null) {
              setCancelButtonText(e.text);
            }
          }
        }
      });
      return () => sub.unsubscribe();
    }, [event$]);

    const onDismiss = usePersistFn(() => {
      navigation.goBack();
      delayed(() => {
        onClosed?.();
        nextAction.current?.();
        nextAction.current = undefined;
      }, 100);
    });

    return (
      <Dialog.Container
        visible={visible}
        useNativeDriver
        onRequestClose={onClose}
        onDismiss={onDismiss}
      >
        <Dialog.Title>{title}</Dialog.Title>
        {message ? <Dialog.Description>{message}</Dialog.Description> : null}
        <Dialog.Input
          value={value}
          placeholder={placeholder}
          onChangeText={onChangeText}
          autoFocus
          keyboardType={keyboardType}
          loading={loading}
          errorMsg={
            error
              ? typeof error === 'string'
                ? error
                : (error as any).message ?? 'Unknown error'
              : undefined
          }
          errorStyle={{ textAlign: 'center' }}
          style={[
            {
              paddingRight: rpx(32),
            },
            inputStyle,
          ]}
        />
        <Dialog.Button
          label={cancelButtonText}
          onPress={handleCancel}
          disabled={loading || cancelButtonDisabled}
          color={loading || cancelButtonDisabled ? '#ccc' : undefined}
        />
        <Dialog.Button
          label={confirmButtonText}
          color={
            destructive
              ? StatusColors.Danger
              : loading || confirmButtonDisabled
              ? '#ccc'
              : undefined
          }
          onPress={handleConfirm}
          disabled={
            loading || confirmButtonDisabled || (required && !value?.trim())
          }
        />
      </Dialog.Container>
    );
  },
  {
    headerShown: false,
    animationEnabled: false,
    cardStyle: { backgroundColor: 'transparent' },
    presentation: 'transparentModal',
  },
);
